import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { OrganicFood, Cart, Clock, Flash, NavArrowRight, Restart, Sparks, Calendar, CheckCircle, ChatLines } from "iconoir-react";
import { fetchToday, updateChore, toggleShopping, firstName } from "@/lib/api";
import { avatarColor, initials } from "@/pages/MiloApp";
import { useT } from "@/lib/i18n";

export default function TodayTab({ session, members, user, onRefresh, onGoto }) {
    const { t, lang } = useT();
    const [data, setData] = useState(null);

    const load = async () => {
        try {
            const res = await fetchToday(session.household_id);
            setData(res);
        } catch (e) {
            console.error(e);
        }
    };
    useEffect(() => {
        load();
    }, [session.household_id]);

    const nameOf = (mid) => firstName(members.find((m) => m.id === mid)?.name) || "—";
    const memberOf = (mid) => members.find((m) => m.id === mid);

    const completeChore = async (id) => {
        await updateChore(session.household_id, id, { status: "done" });
        await load();
        onRefresh?.();
    };

    if (!data) return <div className="text-sm text-zinc-500">{t("today.loading")}</div>;

    const meal = data.meal;
    const chores = data.pending_chores_today || [];
    const shopping = data.shopping_preview || [];
    const shoppingTotal = data.shopping_total ?? shopping.length;
    // Hide the welcome card once dismissed in this browser
    const dismissedKey = `milo_welcome_dismissed_${session.household_id}`;
    const isWelcomeDismissed = typeof window !== "undefined" && localStorage.getItem(dismissedKey) === "1";
    const isFreshHousehold = !meal && shoppingTotal === 0 && (data.all_pending_chores?.length ?? chores.length) === 0;
    const showWelcome = isFreshHousehold && !isWelcomeDismissed;
    const dismissWelcome = () => {
        localStorage.setItem(dismissedKey, "1");
        // Force re-render by updating local data ref (simplest: trigger reload)
        load();
    };

    const now = new Date();
    const locale = lang === "fr" ? "fr-FR" : "en-US";
    const weekday = now.toLocaleDateString(locale, { weekday: "long" });
    const monthDay = now.toLocaleDateString(locale, { month: "long", day: "numeric", year: "numeric" });
    const dateLabel = `${t("today.eyebrow_prefix")} · ${weekday}, ${monthDay}`;
    const displayName = firstName(user?.name) || firstName(session.member_name);

    return (
        <div data-testid="today-tab" className="space-y-5">
            {/* First-run welcome — only when household has no content */}
            {showWelcome && (
                <WelcomeCard
                    name={displayName}
                    householdName={session.household_name}
                    onGoto={onGoto}
                    onDismiss={dismissWelcome}
                />
            )}

            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <div className="text-xs font-semibold text-[#5b6d63] uppercase tracking-widest">{dateLabel}</div>
                <div className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight mt-1">
                    {t("today.title")}
                </div>
                {/* Summary chips */}
                <div className="flex flex-wrap gap-2 mt-4">
                    <Chip tone="sage" label={meal ? t("today.chip.meal_assigned") : t("today.chip.no_meal")} />
                    <Chip tone="butter" label={t(chores.length === 1 ? "today.chip.chore_due" : "today.chip.chores_due", { n: chores.length })} />
                    <Chip tone="rose" label={t(shoppingTotal === 1 ? "today.chip.shopping_item" : "today.chip.shopping_items", { n: shoppingTotal })} />
                </div>
            </motion.div>

            {/* Featured meal */}
            <MealCard meal={meal} memberOf={memberOf} onGoto={onGoto} t={t} />

            {/* Chores due */}
            <SectionCard
                title={t("today.chores_due_today")}
                action={chores.length > 2 ? { label: t("today.see_all"), onClick: () => onGoto?.("chores") } : null}
            >
                {chores.length === 0 ? (
                    <EmptyRow label={t("today.nothing_pressing")} />
                ) : (
                    <ul className="space-y-2">
                        {chores.slice(0, 3).map((c) => (
                            <ChoreRow
                                key={c.id}
                                chore={c}
                                member={memberOf(c.assigned_member_id)}
                                onComplete={() => completeChore(c.id)}
                                t={t}
                            />
                        ))}
                    </ul>
                )}
            </SectionCard>

            {/* Shopping preview */}
            <SectionCard
                title={t("today.shopping_preview")}
                action={{ label: t("today.open_list"), onClick: () => onGoto?.("shopping") }}
            >
                {shopping.length === 0 ? (
                    <EmptyRow label={t("today.nothing_needed")} />
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {shopping.slice(0, 6).map((it) => (
                            <span key={it.id} className="chip chip-sage">{it.name}</span>
                        ))}
                        {shoppingTotal > 6 && (
                            <span className="chip">{t("today.more_count", { n: shoppingTotal - 6 })}</span>
                        )}
                    </div>
                )}
            </SectionCard>
        </div>
    );
}

function Chip({ tone, label }) {
    return <span className={`chip chip-${tone}`}>{label}</span>;
}

function SectionCard({ title, children, action }) {
    return (
        <section className="brut-card p-5 sm:p-6">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#5b6d63]">{title}</h2>
                {action && (
                    <button
                        data-testid={`section-action-${title.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={action.onClick}
                        className="text-xs font-semibold text-[#2F6B43] hover:underline flex items-center gap-1"
                    >
                        {action.label} <NavArrowRight className="w-3 h-3" />
                    </button>
                )}
            </div>
            {children}
        </section>
    );
}

function MealCard({ meal, memberOf, onGoto, t }) {
    if (!meal) {
        return (
            <section className="brut-card p-5 sm:p-6 bg-gradient-to-br from-[#fbeecb] to-[#f8e0a8]">
                <h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#7a5c1a] mb-2">{t("today.todays_meal")}</h2>
                <div className="font-display text-2xl font-semibold mb-3">{t("today.nothing_planned")}</div>
                <button
                    data-testid="goto-chat-from-today-btn"
                    onClick={() => onGoto?.("chat")}
                    className="brut-btn-secondary brut-btn text-sm"
                >
                    <OrganicFood className="w-4 h-4" /> {t("today.tell_milo")}
                </button>
            </section>
        );
    }
    const cook = memberOf(meal.assigned_member_id);
    return (
        <section className="brut-card p-5 sm:p-6 bg-gradient-to-br from-[#fbeecb] to-[#f8e0a8]">
            <div className="flex items-center justify-between mb-2">
                <h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#7a5c1a]">{t("today.todays_meal")}</h2>
                <OrganicFood className="w-4 h-4 text-[#7a5c1a]" />
            </div>
            <div className="font-display text-xl font-semibold leading-tight tracking-tight mb-3">
                {meal.meal_name}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#4a3c1a]">
                {cook && (
                    <div className="flex items-center gap-1.5">
                        <div
                            className="avatar !w-6 !h-6 !text-[10px] !border-[#fbeecb]"
                            style={{ background: avatarColor(cook.name || cook.id) }}
                        >
                            {initials(cook.name)}
                        </div>
                        <span className="font-semibold">{t("today.cook_label", { name: firstName(cook.name) })}</span>
                    </div>
                )}
            </div>
        </section>
    );
}

function ChoreRow({ chore, member, onComplete, t }) {
    const isUrgent = chore.due_date && new Date(chore.due_date) <= new Date();
    return (
        <li className="flex items-center gap-3 py-1.5">
            <button
                data-testid={`complete-chore-${chore.id}-btn`}
                onClick={onComplete}
                className="w-5 h-5 rounded-full border-[1.5px] border-[#2F6B43]/40 hover:bg-[#e8f1e4] hover:border-[#2F6B43] transition-colors shrink-0"
                aria-label="done"
            />
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{chore.title}</div>
                <div className="flex items-center gap-2 text-[11px] text-[#5b6d63] mt-0.5">
                    {member && (
                        <span className="flex items-center gap-1">
                            <span
                                className="inline-block w-2 h-2 rounded-full"
                                style={{ background: avatarColor(member.name || member.id) }}
                            />
                            {firstName(member.name)}
                        </span>
                    )}
                    {chore.due_date && (
                        <span className="flex items-center gap-0.5">
                            <Clock className="w-3 h-3" /> {chore.due_date}
                        </span>
                    )}
                    {isUrgent && (
                        <span className="chip chip-rose !py-0.5 !px-2 !text-[10px] !font-bold">
                            <Flash className="w-2.5 h-2.5" /> {t("today.urgent")}
                        </span>
                    )}
                </div>
            </div>
        </li>
    );
}

function EmptyRow({ label }) {
    return <div className="text-sm text-[#5b6d63] py-1">{label}</div>;
}

function WelcomeCard({ name, householdName, onGoto, onDismiss }) {
    const { t } = useT();
    const starters = [
        {
            id: "plan-dinners",
            label: t("welcome.starter.plan_dinners"),
            sub: t("welcome.starter.plan_dinners_sub"),
            icon: Calendar,
            tone: "butter",
            tab: "week",
        },
        {
            id: "add-shopping",
            label: t("welcome.starter.add_shopping"),
            sub: t("welcome.starter.add_shopping_sub"),
            icon: Cart,
            tone: "sage",
            tab: "shopping",
        },
        {
            id: "first-chore",
            label: t("welcome.starter.first_chore"),
            sub: t("welcome.starter.first_chore_sub"),
            icon: CheckCircle,
            tone: "rose",
            tab: "chores",
        },
    ];
    return (
        <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="welcome-card"
            className="relative brut-card p-5 sm:p-6 overflow-hidden bg-gradient-to-br from-[#fff8e3] via-white to-[#e8f1e4]"
        >
            <button
                data-testid="welcome-dismiss-btn"
                onClick={onDismiss}
                className="absolute top-3 right-3 text-[11px] font-semibold text-[#5b6d63] hover:text-[#0d2a1b] tracking-wide"
                aria-label="Dismiss welcome"
            >
                {t("welcome.dismiss")}
            </button>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-[#2F6B43]">
                <Sparks className="w-3.5 h-3.5" />
                {t("welcome.eyebrow")}
            </div>
            <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-tight mt-1">
                {t("welcome.headline", { name, household: householdName || "your household" })}
            </div>
            <div className="text-sm text-[#255636] mt-2">
                {t("welcome.subline")}
            </div>

            <ul className="mt-4 space-y-2">
                {starters.map((s) => {
                    const Icon = s.icon;
                    return (
                        <li key={s.id}>
                            <button
                                data-testid={`welcome-starter-${s.id}-btn`}
                                onClick={() => onGoto?.(s.tab)}
                                className={`group w-full flex items-center gap-3 p-3 rounded-2xl bg-white/80 hover:bg-white border border-[#0d2a1b]/8 hover:border-[#2F6B43]/40 transition-all text-left`}
                            >
                                <div className={`chip chip-${s.tone} !w-9 !h-9 !rounded-full !p-0 flex items-center justify-center shrink-0`}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm text-[#0d2a1b]">{s.label}</div>
                                    <div className="text-[11px] text-[#5b6d63]">{s.sub}</div>
                                </div>
                                <NavArrowRight className="w-4 h-4 text-[#5b6d63] group-hover:text-[#2F6B43] group-hover:translate-x-0.5 transition-all" />
                            </button>
                        </li>
                    );
                })}
            </ul>

            <button
                data-testid="welcome-talk-btn"
                onClick={() => onGoto?.("chat")}
                className="mt-3 w-full flex items-center justify-center gap-2 bg-[#2F6B43] text-white rounded-2xl py-3 font-semibold text-sm hover:bg-[#255636] transition-colors"
            >
                <ChatLines className="w-4 h-4" /> {t("welcome.talk")}
            </button>
        </motion.section>
    );
}
